/**
 * Business Domains API services
 */

import apiClient from './client';
import type { BusinessDomain, BusinessDomainWithCounts, BusinessDomainAccess, Category } from '@/types';

export interface BusinessDomainCreate {
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  sortOrder?: number;
  isVisible?: boolean;
  isActive?: boolean;
}

export interface BusinessDomainUpdate {
  name?: string;
  description?: string;
  color?: string;
  icon?: string;
  sortOrder?: number;
  isVisible?: boolean;
  isActive?: boolean;
}

const transformBusinessDomain = (data: any): BusinessDomain => ({
  id: data.id,
  tenantId: data.tenant_id,
  name: data.name,
  slug: data.slug,
  description: data.description,
  color: data.color || '#6366f1',
  icon: data.icon,
  isVisible: Boolean(data.is_visible),
  sortOrder: data.sort_order || 0,
  isActive: Boolean(data.is_active),
  createdAt: data.created_at,
  updatedAt: data.updated_at,
});

const transformBusinessDomainWithCounts = (data: any): BusinessDomainWithCounts => ({
  ...transformBusinessDomain(data),
  categoryCount: data.category_count ?? 0,
  templateCount: data.template_count ?? 0,
  categories: data.categories?.map((cat: any) => ({
    id: cat.id,
    tenantId: cat.tenant_id,
    name: cat.name,
    description: cat.description,
    color: cat.color,
    icon: cat.icon,
    sortOrder: cat.sort_order,
    isActive: Boolean(cat.is_active),
    createdAt: cat.created_at,
    updatedAt: cat.updated_at,
  })),
});

const transformAccess = (data: any): BusinessDomainAccess => ({
  id: data.id,
  domainId: data.domain_id,
  tenantUserId: data.tenant_user_id,
  canView: Boolean(data.can_view),
  canEdit: Boolean(data.can_edit),
  canAdmin: Boolean(data.can_admin),
  createdAt: data.created_at,
  userId: data.user_id,
  fullName: data.full_name,
  email: data.email,
});

export const businessDomainsApi = {
  /**
   * List all business domains
   */
  list: async (params?: { includeHidden?: boolean; includeInactive?: boolean }): Promise<BusinessDomainWithCounts[]> => {
    const response = await apiClient.get('/v1/business-domains', {
      params: {
        include_hidden: params?.includeHidden,
        include_inactive: params?.includeInactive,
      },
    });
    const items = Array.isArray(response.data) ? response.data : (response.data.items || []);
    return items.map(transformBusinessDomainWithCounts);
  },

  /**
   * List all business domains with their linked categories
   */
  listWithCategories: async (): Promise<BusinessDomainWithCounts[]> => {
    const response = await apiClient.get('/v1/business-domains/with-categories');
    const items = response.data.items || response.data || [];
    return items.map(transformBusinessDomainWithCounts);
  },

  /**
   * Get a single business domain with details
   */
  get: async (id: string): Promise<BusinessDomainWithCounts & { accessList: BusinessDomainAccess[] }> => {
    const response = await apiClient.get(`/v1/business-domains/${id}`);
    return {
      ...transformBusinessDomainWithCounts(response.data),
      accessList: (response.data.access_list || []).map(transformAccess),
    };
  },

  /**
   * Create a new business domain
   */
  create: async (data: BusinessDomainCreate): Promise<BusinessDomain> => {
    const response = await apiClient.post('/v1/business-domains', {
      name: data.name,
      description: data.description,
      color: data.color || '#6366f1',
      icon: data.icon,
      sort_order: data.sortOrder || 0,
      is_visible: data.isVisible ?? true,
      is_active: data.isActive ?? true,
    });
    return transformBusinessDomain(response.data);
  },

  /**
   * Update a business domain
   */
  update: async (id: string, data: BusinessDomainUpdate): Promise<BusinessDomain> => {
    const payload: any = {};
    if (data.name !== undefined) payload.name = data.name;
    if (data.description !== undefined) payload.description = data.description;
    if (data.color !== undefined) payload.color = data.color;
    if (data.icon !== undefined) payload.icon = data.icon;
    if (data.sortOrder !== undefined) payload.sort_order = data.sortOrder;
    if (data.isVisible !== undefined) payload.is_visible = data.isVisible;
    if (data.isActive !== undefined) payload.is_active = data.isActive;

    const response = await apiClient.patch(`/v1/business-domains/${id}`, payload);
    return transformBusinessDomain(response.data);
  },

  /**
   * Delete a business domain
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/v1/business-domains/${id}`);
  },

  /**
   * Link categories to a business domain
   */
  linkCategories: async (domainId: string, categoryIds: string[]): Promise<{ domainId: string; categories: Category[] }> => {
    const response = await apiClient.post(`/v1/business-domains/${domainId}/categories`, {
      category_ids: categoryIds,
    });
    return {
      domainId: response.data.domain_id,
      categories: (response.data.categories || []).map((cat: any) => ({
        id: cat.id,
        tenantId: cat.tenant_id,
        name: cat.name,
        description: cat.description,
        color: cat.color,
        icon: cat.icon,
        sortOrder: cat.sort_order,
        isActive: Boolean(cat.is_active),
        createdAt: cat.created_at,
        updatedAt: cat.updated_at,
      })),
    };
  },

  /**
   * Unlink a category from a business domain
   */
  unlinkCategory: async (domainId: string, categoryId: string): Promise<void> => {
    await apiClient.delete(`/v1/business-domains/${domainId}/categories/${categoryId}`);
  },

  /**
   * Set access control for a user on a business domain
   */
  setAccess: async (
    domainId: string,
    tenantUserId: string,
    access: { canView?: boolean; canEdit?: boolean; canAdmin?: boolean }
  ): Promise<void> => {
    await apiClient.post(`/v1/business-domains/${domainId}/access`, {
      tenant_user_id: tenantUserId,
      can_view: access.canView ?? true,
      can_edit: access.canEdit ?? false,
      can_admin: access.canAdmin ?? false,
    });
  },

  /**
   * Remove access control for a user on a business domain
   */
  removeAccess: async (domainId: string, tenantUserId: string): Promise<void> => {
    await apiClient.delete(`/v1/business-domains/${domainId}/access/${tenantUserId}`);
  },
};
